'use client'

import React, { useEffect } from 'react'
import { Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { usePage, WhenVisible } from '@inertiajs/react'
import { Button } from '@/Components/ui/button'
import { Card } from '@/Components/ui/card'
import { Input } from '@/Components/ui/input'
import { Skeleton } from '@/Components/ui/skeleton'
import { motion, AnimatePresence } from 'framer-motion'
import type { LanguageCode, Rate } from '@/types'
import { LazySvg } from './lazy-svg'
import { useTranslation } from 'react-i18next'

// Rate Card Component
const RateCard: React.FC<Rate & { index: number }> = ({
  country,
  calling_code,
  icon,
  rate,
  index,
}) => {
  const { i18n } = useTranslation()
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.03 }}
    >
      <Card
        className="flex min-w-[280px] items-center gap-4 p-4 transition-shadow hover:shadow-md lg:min-w-[240px]"
        dir={i18n.dir()}
      >
        <div className="flex-shrink-0">
          {icon ? (
            <LazySvg
              name={`/flags/${icon}.svg`}
              className="h-10 w-10 rounded-sm"
              containsPath={true}
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-primary/10 ring-1 ring-inset ring-primary/10">
              {country['en']?.substring(0, 2)}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-grow">
          <h3 className="truncate text-sm font-medium text-foreground">
            {country[i18n.language as LanguageCode]}
          </h3>
          <p className="text-sm text-muted-foreground">
            <span className="text-primary dark:text-foreground ltr:inline-block rtl:hidden">+</span>
            {calling_code}
            <span className="text-primary dark:text-foreground ltr:hidden rtl:inline-block">+</span>
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center justify-end">
          <LazySvg name="/icons/currency-alt.svg" className="size-3 rounded-sm" />
          <p className="text-sm font-medium tabular-nums">{rate}</p>
        </div>
      </Card>
    </motion.div>
  )
}

// Search Component
const SearchBox: React.FC<{
  value: string
  onChange: (value: string) => void
}> = ({ value, onChange }) => {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search rates by country name or calling code ..."
        className="h-12 rounded-xl border-border pl-9 transition-transform duration-75 focus:border-transparent focus:ring-inset"
      />
    </div>
  )
}

// Custom Pagination Component
const PaginationControls: React.FC<{
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}> = ({ currentPage, totalPages, onPageChange }) => {
  const pageNumbers = React.useMemo(() => {
    const pages = []
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
        pages.push(i)
      } else if (i === currentPage - 2 || i === currentPage + 2) {
        pages.push('...')
      }
    }
    return pages
  }, [currentPage, totalPages])

  return (
    <div className="mt-6 flex items-center justify-center space-x-2">
      <Button
        className="border-border"
        variant="outline"
        size="icon"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      {pageNumbers.map((page, index) => (
        <React.Fragment key={index}>
          {typeof page === 'number' ? (
            <Button
              variant={currentPage === page ? 'default' : 'outline'}
              onClick={() => onPageChange(page)}
              className={`hidden border-border sm:inline-flex ${currentPage === page ? 'bg-primary text-primary-foreground' : ''}`}
            >
              {page}
            </Button>
          ) : (
            <span className="hidden px-2 sm:inline-flex">...</span>
          )}
        </React.Fragment>
      ))}
      <Button
        className="border-border"
        variant="outline"
        size="icon"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  )
}

// Main Component
const IntlRateComponent: React.FC = () => {
  const { i18n } = useTranslation()

  const rates = usePage().props.rates as Rate[]
  const [search, setSearch] = React.useState('')
  const [currentPage, setCurrentPage] = React.useState(1)
  const [itemsPerPage, setItemsPerPage] = React.useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 640 ? 6 : 12
  )

  React.useEffect(() => {
    const handleResize = () => {
      const newItemsPerPage = window.innerWidth < 640 ? 6 : 12
      if (newItemsPerPage !== itemsPerPage) {
        setItemsPerPage(newItemsPerPage)
        setCurrentPage(1) // Reset to first page when layout changes
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [itemsPerPage])

  // Filter countries based on search (including calling codes)
  const filteredRates = React.useMemo(() => {
    return rates.filter((rate) => {
      return (
        rate.country['en']?.toLowerCase()?.includes(search.toLowerCase()) ||
        rate.country['fa']?.toLowerCase()?.includes(search.toLowerCase()) ||
        rate.country['ps']?.toLowerCase()?.includes(search.toLowerCase()) ||
        rate.calling_code.includes(search)
      )
    })
  }, [rates, search])

  // Calculate pagination
  const paginatedRates = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    return filteredRates.slice(startIndex, startIndex + itemsPerPage)
  }, [filteredRates, currentPage, itemsPerPage])

  // Reset to first page when search changes
  React.useEffect(() => {
    setCurrentPage(1)
  }, [search])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const totalPages = React.useMemo(
    () => Math.ceil(filteredRates.length / itemsPerPage),
    [filteredRates, itemsPerPage]
  )

  return (
    <div className="mx-auto w-full px-6">
      <div className="mx-auto mb-8 max-w-lg sm:min-w-[512px]">
        <SearchBox value={search} onChange={setSearch} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentPage}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="grid min-h-[256px] grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          {paginatedRates.map((rate, index) => (
            <RateCard key={`${rate.calling_code}-${index}`} {...rate} index={index} />
          ))}
        </motion.div>
      </AnimatePresence>

      {filteredRates.length > itemsPerPage && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {filteredRates.length === 0 && (
        <p className="mt-8 max-w-screen-lg text-center text-muted-foreground">
          No countries found matching your search.
        </p>
      )}
    </div>
  )
}

// Skeleton loader for a single rate card
const RateCardSkeleton: React.FC = () => (
  <Card className="flex items-center gap-4 p-4">
    <Skeleton className="h-10 w-10 rounded-sm dark:bg-primary-foreground/20" />
    <div className="flex-grow">
      <Skeleton className="mb-2 h-4 w-24 dark:bg-primary-foreground/20" />
      <Skeleton className="h-3 w-16 dark:bg-primary-foreground/20" />
    </div>
    <Skeleton className="h-4 w-16 dark:bg-primary-foreground/20" />
  </Card>
)

// Skeleton loader for the entire component
const IntlRatesSkeleton: React.FC = () => (
  <div className="container mx-auto max-w-7xl px-4 py-8">
    <div className="mx-auto mb-8 max-w-md">
      <Skeleton className="h-10 w-full dark:bg-primary-foreground/20" />
    </div>

    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {[...Array(12)].map((_, index) => (
        <RateCardSkeleton key={index} />
      ))}
    </div>

    <div className="mt-6 flex items-center justify-center space-x-2">
      <Skeleton className="h-10 w-10 rounded-md dark:bg-primary-foreground/20" />
      <Skeleton className="h-10 w-10 rounded-md dark:bg-primary-foreground/20" />
      <Skeleton className="h-10 w-10 rounded-md dark:bg-primary-foreground/20" />
      <Skeleton className="h-10 w-10 rounded-md dark:bg-primary-foreground/20" />
      <Skeleton className="h-10 w-10 rounded-md dark:bg-primary-foreground/20" />
    </div>
  </div>
)

export default function IntlRates() {
  return (
    <WhenVisible data="rates" fallback={<IntlRatesSkeleton />}>
      <IntlRateComponent />
    </WhenVisible>
  )
}
